import moment from 'moment'
import { path, reject } from 'ramda'

import { getCountryCallingCode } from 'libphonenumber-js'

import {
  APP_VERSION,
  LOGIN_TYPES,
  SESSION_STORAGE_KEYS,
} from '@/lib/consts.ts'

import {
  BUSINESS_STATUS_NAMES,
  DEVICE,
  DEVICE_TYPE,
  ONBOARDING_USER_ROLE,
  REGISTRATION_METHODS,
  BUSINESS_CATEGORIES_MARKETING_SCORE,
} from '@/lib/analytics/consts.ts'

import { useStorage } from '@/composables/storage'

const storage = useStorage()

export function capitalizeFirstLetter (s) {
  if (s) {
    return s.charAt(0).toUpperCase() + s.slice(1)
  }

  return null
}

export function getDeviceType (device) {
  const isMobile = [DEVICE.android, DEVICE.ios].includes(device)

  if (isMobile && isTablet()) {
    return DEVICE_TYPE.Tablet
  }

  return DEVICE_TYPE[device] || DEVICE_TYPE[DEVICE.www]
}

export function isTablet () {
  const userAgent = navigator.userAgent.toLowerCase()

  if (/(iphone)/.test(userAgent)) {
    return false
  }

  const regexp = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(ip|ap|wp))))/
  const isValid = regexp.test(userAgent)

  if (isValid) {
    return true
  }

  // validation for iOS 13 and newer
  return /(mac)/.test(userAgent) && navigator.maxTouchPoints > 0
}

export function isWindows () {
  return /(Win)/.test(navigator.appVersion)
}

// get only real categories created by the user
export function getCategories (categories) {
  categories = categories || []
  const filteredCategories = categories.filter(category => category && category.id)
  return filteredCategories.map(simplifyCategory)
}

// used to send only basic information about category
export function simplifyCategory (category) {
  return path(['internal_name'], category)
}

export function getAddress (location) {
  location = location || {}

  return reject((val) => !val, [
    location.address,
    location.address2,
    location.address3,
    location.zipcode,
    location.city,
  ]).join(' ')
}

export function getExternalId ({ id, country, routingConfig }) {
  if (!id) {
    return null
  }

  const deploymentLevel = path(['deployment_level'], routingConfig)

  if (deploymentLevel === 'live') {
    return `${country}-${id}`
  }

  return `${deploymentLevel}-${country}-${id}`
}

export function getBusinessCustomerInfoId ({ customer, country, routingConfig }) {
  if (!customer) {
    return undefined
  }

  const customerId = customer.merged_data?.id ?? customer.id ?? customer._id
  if (customerId == null || customerId === '') {
    return undefined
  }

  const countryLower = String(country || '').toLowerCase()
  if (!countryLower) {
    return undefined
  }

  return getExternalId({
    id: customerId,
    country: countryLower,
    routingConfig,
  })
}

/**
 * `{country}-{id}` without `deployment_level` prefix (Agentforce / Salesforce pre-chat).
 * @see PET-3399
 */
export function getExternalIdForAgentforcePrechat ({ id, country }) {
  if (!id) {
    return null
  }
  if (country == null || country === '') {
    return null
  }
  return `${String(country).toLowerCase()}-${id}`
}

export function getOwnerId ({ business, country, routingConfig, user }) {
  const id = path(['owner_id'], business) || path(['id'], user)
  return getExternalId({ id, country, routingConfig })
}

export function getUserPrefixedPhone ({ user, country }) {
  return path(['cell_phone_with_prefix'], user)
    || getPhone({
      phone: path(['cell_phone'], user),
      country,
    })
}

export function getBusinessPrefixedPhone ({ business, country }) {
  return path(['phone_with_prefix'], business)
    || getPhone({
      phone: path(['phone'], business),
      country,
    })
}

export function getPhone ({ phone, country }) {
  phone = (phone + '').replace(/[()]/g, '')

  const isNotNumber = isNaN(parseInt(phone))

  if (isNotNumber) {
    return null
  }

  phone = '' + phone

  const phoneWithoutPrefix = !phone.includes('+')

  if (phoneWithoutPrefix && country) {
    const countryUpper = country.toUpperCase()
    const prefix = `+${getCountryCallingCode(countryUpper)}`

    phone = `${prefix}${phone}`
  }

  return phone.replace(/\s/g, '')
}

export function getDateText (date) {
  if (isNaN(date)) {
    return null
  }

  return moment(date).format('YYYY-MM-DD HH:mm')
}

export function getMaximumTravelDistance (traveling) {
  if (!traveling) {
    return null
  }

  return `${traveling.distance} ${traveling.distance_unit}`
}

export function setOpenedBusinessData ({ business }) {
  const id = path(['id'], business)

  if (!id) {
    return
  }

  const businesses = getOpenedBusinesses()
  businesses[id] = businesses[id] || {}
  businesses[id].lastTime = moment(new Date()).format('YYYY-MM-DD')
  storage.setItem(storage.KEYS.BUSINESSES_OPENED, businesses)
}

export function getOpenedBusinesses () {
  try {
    return storage.getItem(storage.KEYS.BUSINESSES_OPENED) || {}
  } catch (e) {
    return {}
  }
}

export function isBusinessOpenedToday ({ business }) {
  const id = path(['id'], business)

  if (!id) {
    return false
  }

  const businesses = getOpenedBusinesses()
  const lastTime = path([id, 'lastTime'], businesses)
  const today = moment(new Date()).format('YYYY-MM-DD')

  return lastTime === today
}

export function getEventDate () {
  return moment(new Date()).format('YYYYMMDD')
}

export function getBaseProperties ({
  appType,
  business,
  country,
  device,
  fingerprint,
  ios_fingerprint,
  routingConfig,
  user,
}) {
  const logged_in_user_id = getExternalId({ id: path(['id'], user), country, routingConfig }) || null

  const logged_in_user_role = capitalizeFirstLetter(path(['access_level'], user)) || null

  const business_admin_status = getBusinessStatus(business)

  const business_id = getExternalId({
    id: path(['id'], business),
    country,
    routingConfig,
  }) || null

  const business_phone = getBusinessPrefixedPhone({ business, country }) || null

  const device_type = getDeviceType(device) || null

  const email = path(['owner_email'], business) || path(['email'], user) || null

  const user_id = getOwnerId({ business, country, routingConfig, user }) || null

  const properties = {
    app_version: APP_VERSION,
    business_admin_status,
    business_id,
    business_phone,
    country,
    device_type,
    email,
    event_date: getEventDate(),
    fingerprint,
    logged_in_user_id,
    logged_in_user_role,
    user_role: ONBOARDING_USER_ROLE,
    user_id,
  }

  if (ios_fingerprint) {
    properties.ios_fingerprint = ios_fingerprint
  }

  return properties
}

export function getGoogleAnalyticsClientId () {
  return sessionStorage.getItem(SESSION_STORAGE_KEYS.FIREBASE_APP_ID) || null
}

export function getBranchIOFingerprintId () {
  let browserFingerprintId

  try {
    window.branch.getBrowserFingerprintId((err, data) => {
      if (!err) { browserFingerprintId = data }
    })

    return browserFingerprintId
  } catch (e) {
    return null
  }
}

export function getAnalyticsToken () {
  const tokens = []

  const userPseudoId = getGoogleAnalyticsClientId()
  const userBranchIOId = getBranchIOFingerprintId()

  if (userPseudoId) {
    tokens.push(`client_id;${userPseudoId}`)
  }

  if (userBranchIOId) {
    tokens.push(`browser_fingerprint_id;${userBranchIOId}`)
  }

  return tokens.join(';') || null
}

export function getRegistrationMethod (loginType) {
  const defaultRegistrationMethod = REGISTRATION_METHODS[LOGIN_TYPES.DEFAULT]
  return REGISTRATION_METHODS[loginType] || defaultRegistrationMethod
}

export function getBusinessStatus (business) {
  if (!(business?.status in BUSINESS_STATUS_NAMES)) {
    return null
  }

  return BUSINESS_STATUS_NAMES[business.status]
}

export function getBcrConnectInfo (reader) {
  const { device_type, device_sw_version, serial_number, status, label, ip_address } = reader
  return {
    stripe_bcr_connection_reader_type: device_type,
    stripe_bcr_software_version: device_sw_version,
    stripe_bcr_reader_SN: serial_number,
    stripe_bcr_connection_terminal_status: status,
    stripe_bcr_connection_terminal_type: label,
    stripe_bcr_connetion_ip_adress: ip_address || null,
  }
}

export function getAppsflyerUserId () {
  if (!document || typeof document.cookie !== 'string') {
    return null
  }

  const match = document.cookie.match(/afUserId=[-0-9A-Za-z]*/)
  const value = (match && match[0]) || ''

  return value.replace('afUserId=', '') || null
}

export const PAYMENT_METHOD_TYPE = {
  C: 'Credit Card',
  P: 'PayPal',
  V: 'Venmo',
}

export function getPaymentMethodTypeLabel (paymentMethodType) {
  return (paymentMethodType && PAYMENT_METHOD_TYPE[paymentMethodType]) || 'Unknown'
}

export const BUSINESS_STATUS_TYPE = {
  S: 'Setup',
  T: 'Trial',
  F: 'Trial Blocked',
  P: 'Paid Active',
  O: 'Active Overdue',
  L: 'Blocked Overdue',
  R: 'Churned',
  H: 'Suspended',
  B: 'Invalid Business',
}

export function getBusinessStatusLabel (businessStatus) {
  return BUSINESS_STATUS_TYPE[businessStatus] || 'Unknown'
}

export function convertArrayToString (event) {
  if (!event) {
    return
  }

  // shortcut: skip converter
  if (event._sendRaw) {
    const { _sendRaw, ...parameters } = event

    return parameters
  }

  const converted = JSON.parse(JSON.stringify(event))

  Object.keys(converted).forEach(key => {
    if (Array.isArray(converted[key])) {
      converted[key] = converted[key].join(',') // nosemgrep: prototype-pollution-function
    } else if (converted[key] !== null && typeof (converted[key]) === 'object') {
      converted[key] = convertArrayToString(converted[key]) // nosemgrep: prototype-pollution-function
    }
  })

  return converted
}

export function groupInternalNames (internalNames, groupsCount) {
  if (!internalNames?.length) return {}

  const GROUP_LENGTH_MAX = 100
  const groups = []
  let currentGroup = []

  for (const name of internalNames) {
    const tempGroup = [...currentGroup, name].join(',')

    if (tempGroup.length <= GROUP_LENGTH_MAX) {
      currentGroup.push(name)
    } else {
      groups.push(currentGroup.join(','))
      if (groups.length >= groupsCount) break
      currentGroup = [name]
    }
  }

  if (currentGroup.length && groups.length < groupsCount) {
    groups.push(currentGroup.join(','))
  }

  while (groups.length < groupsCount) {
    groups.push('')
  }

  return Object.fromEntries(groups.map((name, index) => [`internal_names_${index + 1}`, name]))
}

export function getBusinessMarketingScore (primaryCategory) {
  return BUSINESS_CATEGORIES_MARKETING_SCORE[primaryCategory] || 0
}
